import React, { useState, useMemo } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FileText, Plus, ChevronRight, ArrowLeft, CheckCircle2, AlertTriangle,
  Search, Edit3, Trash2, Send, DollarSign, Building2, Briefcase,
  Download, X, Save,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, Button, Field, Input, Select, Spinner, EmptyState, KPI,
} from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";
import { splitFromInc } from "@/lib/gst";
import { generatePoPdf, getLatestPoPdfUrl, loadPoSettings, defaultDeliverKind, deliverTextFor, PO_KIND_LABEL, DELIVER_KIND_LABEL } from "./poPdf";   // 27 Oct 2026 — the rebuilt PO PDF (mig 464)
import { Truck, Boxes, Hammer, MoreHorizontal, MapPin, ExternalLink } from "lucide-react";
import { createProjectBooking } from "@/lib/bookingWrites";   // 3 Nov 2026 — one way in for a booking

// =============================================================================
// Purchase Orders — list / new / detail
// =============================================================================

// -----------------------------------------------------------------------------
// List
// -----------------------------------------------------------------------------
export function PosListScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<"open" | "sent" | "paid" | "all">("all");
  const [search, setSearch] = useState("");

  // Global KPI query
  const { data: kpi } = useQuery({
    queryKey: ["po_kpi"],
    queryFn: async () => {
      const { data } = await supabase
        .from("purchase_orders")
        .select("status, total_inc_gst");
      const rows = (data ?? []) as any[];
      return {
        draft:      rows.filter(r => r.status === "draft").length,
        committed:  rows.filter(r => ["sent","acknowledged","received","invoiced"].includes(r.status)).reduce((a,r) => a + Number(r.total_inc_gst ?? 0), 0),
        paid:       rows.filter(r => r.status === "paid").reduce((a,r) => a + Number(r.total_inc_gst ?? 0), 0),
        total:      rows.length,
      };
    },
  });

  const { data: pos = [], isLoading, error } = useQuery({
    queryKey: ["pos_list", filter, search],
    queryFn: async () => {
      let q = supabase
        .from("purchase_orders")
        .select("po_id, po_number, po_title, status, po_date, total_inc_gst, project_id, supplier_id")
        .order("po_date", { ascending: false })
        .limit(200);

      if (filter === "open")  q = q.in("status", ["draft","sent","acknowledged"]);
      if (filter === "sent")  q = q.eq("status", "sent");
      if (filter === "paid")  q = q.eq("status", "paid");
      if (search.trim()) q = q.or(`po_number.ilike.%${search}%,po_title.ilike.%${search}%`);

      const { data, error } = await q;
      if (error) throw error;
      const rows = (data ?? []) as any[];
      if (rows.length === 0) return rows;

      // Hydrate project + supplier names
      const projectIds = [...new Set(rows.map(r => r.project_id).filter(Boolean))];
      const supplierIds = [...new Set(rows.map(r => r.supplier_id).filter(Boolean))];

      const [pRes, sRes] = await Promise.all([
        projectIds.length  ? supabase.from("projects").select("project_id, project_code").in("project_id", projectIds) : Promise.resolve({ data: [] }),
        supplierIds.length ? supabase.from("suppliers").select("supplier_id, legal_name, trading_name").in("supplier_id", supplierIds) : Promise.resolve({ data: [] }),
      ]);
      const pMap = new Map((pRes.data ?? []).map((r: any) => [r.project_id, r]));
      const sMap = new Map((sRes.data ?? []).map((r: any) => [r.supplier_id, r]));

      return rows.map(r => ({
        ...r,
        project: pMap.get(r.project_id) ?? null,
        supplier: sMap.get(r.supplier_id) ?? null,
      }));
    },
  });

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      <Link to="/finance" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14}/> Finance hub
      </Link>
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Purchase Orders</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Commitments to suppliers — plant hire, subbies, materials. Land against a project budget line.
          </div>
        </div>
        {hasRole(user, "manager") && (
          <Link to="/pos/new"><Button icon={Plus}>New PO</Button></Link>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI label="Drafts"   value={kpi?.draft ?? 0} icon={FileText}/>
        <KPI label="Committed"  value={fmtMoney(kpi?.committed ?? 0)} sub="sent → awaiting invoice" />
        <KPI label="Paid"       value={fmtMoney(kpi?.paid ?? 0)} />
        <KPI label="Total POs"  value={kpi?.total ?? 0} />
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-raised border border-line rounded-md flex-1 max-w-md">
          <Search size={14} className="text-text-muted"/>
          <input placeholder="Search PO # or title..." value={search} onChange={e => setSearch(e.target.value)}
            className="bg-transparent outline-none text-text text-[13px] flex-1"/>
        </div>
        <div className="flex items-center gap-2">
          {(["open","sent","paid","all"] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded text-[11px] font-mono uppercase tracking-wider2 font-semibold border transition-colors ${
                filter === f ? "bg-accent/12 text-accent border-accent/30" : "bg-ink-raised text-text-muted border-line hover:text-text"
              }`}>{f}</button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-danger/10 border border-danger/30 rounded-md p-3 text-[12px] text-danger">
          <AlertTriangle size={12} className="inline mr-1.5"/>
          {(error as any).message}
        </div>
      )}

      {isLoading ? <div className="py-10 flex justify-center"><Spinner/></div>
       : pos.length === 0 ? <Card><EmptyState title="No POs yet" description="Click 'New PO' to issue your first one." icon={FileText}/></Card>
       : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead><tr className="border-b border-line">
                  {["PO #", "Title", "Supplier", "Project", "Date", "Total inc GST", "Status", ""].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {pos.map((p, i) => (
                    <tr key={p.po_id} className={`border-b border-line cursor-pointer hover:bg-ink-hover ${i % 2 ? "bg-ink/40" : ""}`}
                      onClick={() => navigate(`/pos/${p.po_id}`)}>
                      <td className="px-4 py-3 font-mono text-[11px] text-accent">{p.po_number}</td>
                      <td className="px-4 py-3 max-w-[280px] truncate">{p.po_title}</td>
                      <td className="px-4 py-3 text-[12px]">{p.supplier?.legal_name ?? "—"}</td>
                      <td className="px-4 py-3 font-mono text-[11px] text-text-muted">{p.project?.project_code ?? "—"}</td>
                      <td className="px-4 py-3 font-mono text-[12px] text-text-muted">{fmtDate(p.po_date)}</td>
                      <td className="px-4 py-3 font-mono">{fmtMoney(p.total_inc_gst)}</td>
                      <td className="px-4 py-3"><Pill label={p.status} tone={statusTone(p.status)} small/></td>
                      <td className="px-4 py-3"><ChevronRight size={14} className="text-text-dim"/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
    </div>
  );
}

function statusTone(s: string): any {
  switch (s) {
    case "draft":        return "pending";
    case "sent":         return "mobilising";
    case "acknowledged": return "mobilising";
    case "received":     return "bid";
    case "invoiced":     return "warning";
    case "paid":         return "won";
    case "cancelled":    return "lost";
    default:             return "default";
  }
}

// -----------------------------------------------------------------------------
// New PO picker — pick project first, then supplier
// -----------------------------------------------------------------------------
export function NewPoScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  // 28 Oct 2026 — "Raise a PO" on a budget envelope lands here with the job and code set
  const [sp] = useSearchParams();
  const [project_id, setProjectId] = useState(sp.get("project_id") ?? "");
  const [supplier_id, setSupplierId] = useState(sp.get("supplier_id") ?? "");
  const [po_title, setPoTitle] = useState("");
  // 28 Oct 2026 (mig 474) — "Raise a PO" on an accepted supplier quote lands
  // here with the register row: supplier, title, kind and code are set and the
  // PO gets ONE line — "<title> as per your quote <ref>" at their price. James:
  // "a PO is normally one line referencing the quote or item the PO is for."
  const supplier_quote_id = sp.get("supplier_quote_id");
  const { data: sq } = useQuery({
    queryKey: ["supplier_quote_for_po", supplier_quote_id], enabled: !!supplier_quote_id,
    queryFn: async () => { const { data } = await (supabase as any).from("v_supplier_quote_register").select("*").eq("supplier_quote_id", supplier_quote_id).maybeSingle(); return data as any | null; },
  });
  React.useEffect(() => {
    if (!sq) return;
    if (!supplier_id) setSupplierId(sq.supplier_id);
    if (!po_title) setPoTitle(sq.title ?? "");
    if (sq.budget_code) setBudgetCode(sq.budget_code);
    const k = sq.supplier_kind === "plant_hire" ? "plant" : sq.supplier_kind === "general" ? "materials" : (sq.supplier_kind === "subcontractor" || sq.supplier_kind === "labour_hire" || sq.supplier_kind === "structure") ? "labour" : null;
    if (k) setPoKind(k as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sq]);
  // 27 Oct 2026 — what kind of PO. Picks the deliver-to (plant → site,
  // materials → depot, labour → office — James's rule) and the booking type.
  const [po_kind, setPoKind] = useState<"plant" | "materials" | "labour" | "other">("plant");
  // 28 Oct 2026 (mig 471) — the code this order consumes; defaulted by kind, always a pick
  const [budget_code, setBudgetCode] = useState<string>(sp.get("budget_code") ?? "");
  const { data: budgetCodes = [] } = useQuery({
    queryKey: ["budget_codes_project"],
    queryFn: async () => { const { data } = await (supabase as any).from("budget_codes").select("budget_code, display_name, category, meaning, applies_to, sort_order").eq("is_active", true).order("sort_order"); return ((data ?? []) as any[]).filter(c => (c.applies_to ?? ["project"]).includes("project")); },
  });
  // what that code has left on this job, shown as you pick (mig 470 view)
  const { data: pickRow } = useQuery({
    queryKey: ["po_new_budget_row", project_id, budget_code], enabled: !!project_id && !!budget_code,
    queryFn: async () => { const { data } = await (supabase as any).from("v_project_budget_codes").select("current_ex, committed_ex, actual_ex, remaining_ex").eq("project_id", project_id).eq("budget_code", budget_code).maybeSingle(); return data as any | null; },
  });
  const defaultCodeFor = (k: string) => { const cat = k === "plant" ? "plant" : k === "materials" ? "materials" : k === "labour" ? "subcontract" : null; return cat ? (budgetCodes.find((c: any) => c.category === cat)?.budget_code ?? "") : ""; };
  const KIND_TILES: { key: typeof po_kind; label: string; sub: string; icon: any }[] = [
    { key: "plant",     label: "Plant / hire",         sub: "crane, EWP, truck — delivers to the site",      icon: Truck },
    { key: "materials", label: "Materials",            sub: "supply only — delivers to the depot",          icon: Boxes },
    { key: "labour",    label: "Labour / subcontract", sub: "sparkie, rigger, trade — the depot on the PO",  icon: Hammer },
    { key: "other",     label: "Other",                sub: "pick the deliver-to yourself",                 icon: MoreHorizontal },
  ];

  // T-080 — optional on-site booking capture. PO-relevant types only —
  // we don't raise POs against carrier integration / internal meetings /
  // inspections, so those aren't offered here. Trade work (Electrician,
  // etc.) maps to booking_type='subcontractor' in the DB so the calendar
  // colour scheme stays consistent with bookings added via Schedule.
  const [booking_type, setBookingType] = useState<"" | "plant" | "delivery" | "subcontractor" | "other">("");

  // Display-label map — what the user picks vs what we store. "Electrician"
  // is the most common subbie PO type Coretel raises so it's the lead label.
  // If we need finer trade taxonomy later (Concreter, Surveyor, Traffic
  // Management, etc.) we promote this to a real per-trade enum.
  const PO_BOOKING_OPTIONS: Array<{ value: typeof booking_type; label: string }> = [
    { value: "",              label: "No booking — just raise the PO" },
    { value: "subcontractor", label: "Electrician / trade on site" },
    { value: "plant",         label: "Plant on site" },
    { value: "delivery",      label: "Delivery" },
    { value: "other",         label: "Other" },
  ];
  const [on_site_start, setOnSiteStart] = useState<string>(""); // yyyy-MM-dd
  const [on_site_end,   setOnSiteEnd]   = useState<string>(""); // yyyy-MM-dd

  const wantsBooking = booking_type !== "" && !!on_site_start;

  const { data: projects = [] } = useQuery({
    queryKey: ["projects_for_po"],
    queryFn: async () => (await supabase
      .from("projects")
      .select("project_id, project_code, project_name")
      .in("status", ["scheduled","mobilising","live","on_hold","acceptance_pending"])
      .order("project_code", { ascending: false })
      .limit(200)).data ?? [],
  });

  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers_for_po"],
    queryFn: async () => (await supabase
      .from("suppliers")
      .select("supplier_id, legal_name, trading_name")
      .eq("is_active", true)
      .order("legal_name")).data ?? [],
  });

  const createMut = useMutation({
    mutationFn: async () => {
      if (!project_id) throw new Error("Project required");
      if (!supplier_id) throw new Error("Supplier required");
      if (!po_title.trim()) throw new Error("Title required");
      if (!budget_code) throw new Error("Pick the budget code this order spends.");
      if (booking_type !== "" && !on_site_start) {
        throw new Error("Pick a start date for the on-site booking, or set booking type back to 'No booking'.");
      }
      if (on_site_start && on_site_end && on_site_end < on_site_start) {
        throw new Error("On-site end date must be the same as or after the start date.");
      }

      const { data: { user: authUser } } = await supabase.auth.getUser();

      // 27 Oct 2026 — the deliver-to and the printed terms are set now so the
      // PDF is right first time; all editable on the PO.
      const [{ data: proj }, settings, { data: sup }] = await Promise.all([
        (supabase as any).from("projects").select("site_id, project_manager:project_manager_id(first_name, last_name, mobile), site_lead:site_lead_id(first_name, last_name, mobile), supervisor:supervisor_id(first_name, last_name, mobile)").eq("project_id", project_id).maybeSingle(),
        loadPoSettings(),
        supabase.from("suppliers").select("payment_terms_days").eq("supplier_id", supplier_id).maybeSingle(),
      ]);
      const site = proj?.site_id ? (await supabase.from("sites").select("site_code, site_name, address_line1, address_line2, suburb, state, postcode").eq("site_id", proj.site_id).maybeSingle()).data : null;
      const dKind = defaultDeliverKind(po_kind);
      // 27 Oct — James: "the phone should be the project manager, so if Clint him, if me it's me or Josh"
      const contact = (proj as any)?.project_manager ?? (proj as any)?.site_lead ?? (proj as any)?.supervisor ?? null;

      // 1. Create the PO first.
      const { data: poRow, error: poErr } = await (supabase as any)
        .from("purchase_orders")
        .insert({
          project_id,
          supplier_id,
          site_id:     proj?.site_id ?? null,
          po_title:    po_title.trim(),
          po_kind,
          budget_code,
          supplier_quote_id: sq?.supplier_quote_id ?? null,   // mig 474
          quote_id:          sq?.quote_id ?? null,
          deliver_to_kind: dKind,
          deliver_to_text: deliverTextFor(dKind, site, settings),
          payment_terms_text: sup?.payment_terms_days ? `${sup.payment_terms_days} days from invoice` : (settings.po_default_payment_terms || "30 days from invoice"),
          coretel_contact_name: contact ? [contact.first_name, contact.last_name].filter(Boolean).join(" ") : null,
          coretel_contact_phone: contact?.mobile ?? null,
          status:      "draft",
          po_date:     new Date().toISOString().slice(0, 10),
          created_by:  authUser?.id ?? null,
          updated_by:  authUser?.id ?? null,
        })
        .select("po_id")
        .single();
      if (poErr) throw poErr;
      const poId = poRow.po_id;

      // 1b. Raised from a supplier quote → the one line, at their price, on the code
      if (sq && Number(sq.amount_ex) > 0) {
        const when = sq.received_at ? ` dated ${new Date(sq.received_at).toLocaleDateString("en-AU")}` : "";
        const { error: lnErr } = await (supabase as any).from("purchase_order_lines").insert({
          po_id: poId,
          description: `${sq.title} — as per your quote${sq.supplier_ref ? ` ${sq.supplier_ref}` : ""}${when}`,
          quantity: 1,
          unit: "lump_sum",
          unit_price_ex_gst: Number(Number(sq.amount_ex).toFixed(2)),
          gst_applies: true,
          budget_code: budget_code,
          sort_order: 0,
        });
        if (lnErr) throw new Error(`PO created but its line failed: ${lnErr.message}. Add the line on the PO.`);
      }

      // 2. If on-site dates were provided, also create the project_bookings
      //    row and link it back via created_booking_id. Booking dates use
      //    the project_bookings.scheduled_start/end timestamptz columns;
      //    we anchor to 07:00 local for site-arrival convention.
      if (wantsBooking) {
        // 3 Nov 2026 — through the one helper (07:00–17:00 local, tentative, a history row); the PO link is kept
        let bookingId: string;
        try {
          bookingId = await createProjectBooking({ project_id, booking_type, title: po_title, description: `Auto-created from PO at ${new Date().toISOString().slice(0,10)}.`, day: on_site_start, end_day: on_site_end || null, supplier_id }, "Created with the PO", user?.user_id ?? null);
        } catch (e: any) {
          // PO is in, booking failed. Surface clearly so user can re-try via Schedule.
          throw new Error(`PO created but booking failed: ${e?.message ?? e}. Add the booking manually via the project Schedule.`);
        }

        // 3. Patch the PO with the booking FK so we trace the link both ways.
        await supabase
          .from("purchase_orders")
          .update({ created_booking_id: bookingId })
          .eq("po_id", poId);
      }

      return poId;
    },
    onSuccess: (id) => navigate(`/pos/${id}`),
  });

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-6">
      <button onClick={() => navigate("/pos")} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft size={14}/> Purchase Orders
      </button>
      <h1 className="text-2xl font-semibold tracking-tight mb-1">New purchase order</h1>
      <div className="text-text-muted text-[13px] mb-5">
        {sq
          ? <>From <span className="text-text">{sq.supplier_short}</span>'s quote{sq.supplier_ref ? <> <span className="font-mono">{sq.supplier_ref}</span></> : null} — one line at <span className="font-mono text-text">{fmtMoney(sq.amount_ex ?? 0)}</span> ex GST goes on the PO; change anything on the next page.</>
          : <>Issue to a supplier against a project. Add line items on the next page.</>}
      </div>

      <Card>
        <div className="p-5 space-y-4">
          <Field label="Project *" hint="Active projects only. Each PO must have a home.">
            <Select value={project_id} onChange={e => setProjectId(e.target.value)}>
              <option value="">— select project —</option>
              {(projects as any[]).map(p => (
                <option key={p.project_id} value={p.project_id}>
                  {p.project_code} · {p.project_name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Supplier *">
            <Select value={supplier_id} onChange={e => setSupplierId(e.target.value)}>
              <option value="">— select supplier —</option>
              {(suppliers as any[]).map(s => (
                <option key={s.supplier_id} value={s.supplier_id}>
                  {s.legal_name}{s.trading_name && s.trading_name !== s.legal_name ? ` (${s.trading_name})` : ""}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="PO title *" hint="Short description — e.g. '60t crane hire, 2 days'">
            <Input value={po_title} onChange={e => setPoTitle(e.target.value)}/>
          </Field>
          <Field label="Budget code *" hint="Which part of the job's budget this order spends. The PO shows what it does to that code before it goes.">
            <Select value={budget_code} onChange={e => setBudgetCode(e.target.value)}>
              <option value="">— pick a code —</option>
              {budgetCodes.map((c: any) => <option key={c.budget_code} value={c.budget_code}>{c.budget_code} · {c.display_name}</option>)}
            </Select>
            {budget_code && project_id && (
              <div className="mt-1.5 text-[12px]">
                {pickRow ? <span className="text-text-muted">On this job: budget <span className="font-mono text-text">{fmtMoney(pickRow.current_ex)}</span> · committed <span className="font-mono text-warn">{fmtMoney(pickRow.committed_ex)}</span> · spent <span className="font-mono text-text">{fmtMoney(pickRow.actual_ex)}</span> · <span className={`font-mono ${Number(pickRow.remaining_ex) < 0 ? "text-danger" : "text-ok"}`}>{fmtMoney(pickRow.remaining_ex)} left</span></span>
                         : <span className="text-text-dim">No budget on this job for that code yet — the PO will show there as spend with no budget line.</span>}
                {budgetCodes.find((c: any) => c.budget_code === budget_code)?.meaning && <div className="text-[11px] text-text-dim">{budgetCodes.find((c: any) => c.budget_code === budget_code).meaning}</div>}
              </div>
            )}
          </Field>
          <Field label="What kind of order" hint="Sets where it delivers and the booking type — both can be changed on the PO.">
            <div className="grid grid-cols-2 gap-2">
              {KIND_TILES.map(t => (
                <button key={t.key} type="button" onClick={() => { setPoKind(t.key); if (t.key === "plant") setBookingType("plant"); else if (t.key === "materials") setBookingType("delivery"); else if (t.key === "labour") setBookingType("subcontractor"); }}
                  className={`text-left p-3 rounded-lg border transition ${po_kind === t.key ? "border-accent bg-accent/10" : "border-line hover:border-line-light"}`}
                  onMouseUp={() => setBudgetCode(defaultCodeFor(t.key))}>
                  <div className="flex items-center gap-2 text-[13px] font-medium"><t.icon size={14} className={po_kind === t.key ? "text-accent" : "text-text-muted"}/>{t.label}</div>
                  <div className="text-[11px] text-text-muted mt-0.5">{t.sub}</div>
                </button>
              ))}
            </div>
          </Field>

          {/* T-080 — optional on-site booking capture. Soft entry; default
              "No booking" so PO creation flow is unchanged when dates aren't
              known. When booking_type is set + start date provided, a
              project_bookings row gets created and linked back via
              purchase_orders.created_booking_id. Adjustable later via
              the project Schedule. */}
          <div className="pt-2 mt-2 border-t border-line">
            <Field
              label="Schedule on-site activity? (optional)"
              hint="If you know when the supplier is on site, capture it here and we'll create the booking automatically. Leave as 'No booking' if unknown — you can always add it later from the project Schedule."
            >
              <Select value={booking_type} onChange={e => setBookingType(e.target.value as any)}>
                {PO_BOOKING_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </Field>

            {booking_type !== "" && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Field label="On-site start" hint="When supplier arrives on site.">
                  <Input
                    type="date"
                    value={on_site_start}
                    onChange={e => setOnSiteStart(e.target.value)}
                  />
                </Field>
                <Field label="On-site end (optional)" hint="Same day = leave blank.">
                  <Input
                    type="date"
                    value={on_site_end}
                    onChange={e => setOnSiteEnd(e.target.value)}
                  />
                </Field>
              </div>
            )}

            {wantsBooking && (
              <div className="mt-2 px-2.5 py-1.5 rounded bg-accent/12 border border-accent/25 text-[11px] text-accent inline-flex items-center gap-1.5">
                <CheckCircle2 size={11}/>
                Booking will be created on save (status: tentative). Edit anytime via the project Schedule.
              </div>
            )}
          </div>

          {createMut.isError && (
            <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">
              <AlertTriangle size={11} className="inline mr-1"/>
              {(createMut.error as any)?.message}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-line bg-ink-raised flex justify-end gap-2">
          <Button variant="secondary" onClick={() => navigate("/pos")}>Cancel</Button>
          <Button icon={CheckCircle2} onClick={() => createMut.mutate()} disabled={createMut.isPending}>
            {createMut.isPending
              ? (wantsBooking ? "Creating PO + booking…" : "Creating…")
              : (wantsBooking ? "Create PO + booking" : "Create & add lines")}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// -----------------------------------------------------------------------------
// PO detail / builder
// -----------------------------------------------------------------------------
export function PoDetailScreen() {
  const { poId } = useParams<{ poId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [pdfBusy, setPdfBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  const { data: po, isLoading, error } = useQuery({
    queryKey: ["po", poId],
    enabled: !!poId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("purchase_orders")
        .select("*")
        .eq("po_id", poId!)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;

      // Pull related rows in parallel — including the optional booking
      // (T-080) so we can show "Linked booking" on the detail screen and
      // jump straight back to the project Schedule from here.
      const [pRes, sRes, siteRes, bRes] = await Promise.all([
        data.project_id ? supabase.from("projects").select("project_code, project_name, client_id").eq("project_id", data.project_id).maybeSingle() : Promise.resolve({ data: null }),
        data.supplier_id ? supabase.from("suppliers").select("*").eq("supplier_id", data.supplier_id).maybeSingle() : Promise.resolve({ data: null }),
        data.site_id ? supabase.from("sites").select("site_code, site_name, address_line1, address_line2, suburb, state, postcode").eq("site_id", data.site_id).maybeSingle() : Promise.resolve({ data: null }),
        data.created_booking_id
          ? supabase.from("project_bookings")
              .select("booking_id, booking_type, title, scheduled_start, scheduled_end, status")
              .eq("booking_id", data.created_booking_id)
              .maybeSingle()
          : Promise.resolve({ data: null }),
      ]);

      return {
        ...data,
        project: pRes.data ?? null,
        supplier: sRes.data ?? null,
        site: siteRes.data ?? null,
        linked_booking: bRes.data ?? null,
      } as any;
    },
  });

  const { data: lines = [] } = useQuery({
    queryKey: ["po_lines", poId],
    enabled: !!poId,
    queryFn: async () => {
      const { data } = await supabase
        .from("purchase_order_lines")
        .select("*")
        .eq("po_id", poId!)
        .order("sort_order");
      return (data ?? []) as any[];
    },
  });

  const { data: budgetLines = [] } = useQuery({
    queryKey: ["po_project_budget_lines", po?.project_id],
    enabled: !!po?.project_id,
    queryFn: async () => {
      const { data } = await supabase
        .from("budget_lines")
        .select("budget_line_id, description, budget_code, category, planned_amount_ex_gst")
        .eq("project_id", po!.project_id)
        .eq("is_active", true)
        .order("sort_order");
      return (data ?? []) as any[];
    },
  });

  // T-450 — invoices coded against this PO, to flag a stale PO vs actual spend.
  const { data: linkedInvoices = [] } = useQuery({
    queryKey: ["po_linked_invoices", poId],
    enabled: !!poId,
    queryFn: async () => {
      const { data } = await supabase
        .from("ap_invoices")
        .select("ap_invoice_id, invoice_number, amount_inc_gst, status")
        .eq("po_id", poId!);
      return (data ?? []) as any[];
    },
  });

  // T-450 — one-click reconcile: add a variance line for the delta so the PO
  // header recomputes to match what was actually invoiced (POs went stale before).
  const matchPoMut = useMutation({
    mutationFn: async (deltaIncGst: number) => {
      if (!poId) return;
      const exAbs = splitFromInc(Math.abs(deltaIncGst)).ex;
      const ex = deltaIncGst < 0 ? -exAbs : exAbs;
      const invNos = (linkedInvoices as any[]).map(i => i.invoice_number).filter(Boolean).join(", ");
      const { error } = await supabase.from("purchase_order_lines").insert({
        po_id: poId,
        description: `Invoice variance${invNos ? ` — ${invNos}` : ""}`,
        quantity: 1,
        unit_price_ex_gst: Number(ex.toFixed(2)),
        gst_applies: true,
        sort_order: lines.length,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["po_lines", poId] });
      qc.invalidateQueries({ queryKey: ["po", poId] });
    },
    onError: (e: any) => window.alert(`Couldn't add the variance line: ${e?.message ?? e}`),
  });

  const addLineMut = useMutation({
    mutationFn: async () => {
      if (!poId) return;
      const { error } = await supabase.from("purchase_order_lines").insert({
        po_id: poId,
        description: "New line",
        quantity: 1,
        unit_price_ex_gst: 0,
        gst_applies: true,
        sort_order: lines.length,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["po_lines", poId] });
      qc.invalidateQueries({ queryKey: ["po", poId] });
    },
    // T-403 — the T-380 lesson reached the status mutations but missed these
    // two siblings: a silently-failed line write is money wrong on a PO.
    onError: (e: any) => alert(`Couldn't add the line: ${e?.message ?? e}`),
  });

  const updateLineMut = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: any }) => {
      const { error } = await supabase.from("purchase_order_lines").update(patch).eq("po_line_id", id);
      if (error) throw error;
    },
    onError: (e: any) => alert(`Couldn't save the line change: ${e?.message ?? e} — the value on screen has NOT been saved.`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["po_lines", poId] });
      qc.invalidateQueries({ queryKey: ["po", poId] });
    },
  });

  const deleteLineMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("purchase_order_lines").delete().eq("po_line_id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["po_lines", poId] });
      qc.invalidateQueries({ queryKey: ["po", poId] });
    },
    onError: (e: any) => window.alert(`Couldn't delete the line: ${e?.message ?? e}`),
  });

  const statusMut = useMutation({
    mutationFn: async (newStatus: string) => {
      const patch: any = { status: newStatus };
      if (newStatus === "sent")     patch.sent_at = new Date().toISOString();
      if (newStatus === "received") patch.received_at = new Date().toISOString();
      const { error } = await supabase.from("purchase_orders").update(patch).eq("po_id", poId!);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["po", poId] }),
    // Surfacing failures — a silent no-op here hid a real error (James, PO-26-0003).
    onError: (e: any) => window.alert(`Couldn't update the PO status: ${e?.message ?? e}${e?.details ? `\n${e.details}` : ""}${e?.hint ? `\nHint: ${e.hint}` : ""}`),
  });

  // Keep budget_spend in sync: when PO → sent, create a committed spend row per line.
  // Simplest implementation: after sending, user gets a button to "Commit to budgets"
  // which writes rows to budget_spend. Skipping auto-commit for now — will add once
  // we've tested the happy path.

  if (isLoading) return <div className="py-20 flex justify-center"><Spinner/></div>;
  if (error) return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-danger/10 border border-danger/30 rounded p-4 text-[13px] text-danger">
        <div className="font-semibold mb-1">Failed to load PO</div>
        <div className="text-[12px]">{(error as any).message}</div>
      </div>
    </div>
  );
  if (!po) return <div className="p-6">PO not found.</div>;

  const canEdit = hasRole(user, "manager") && po.status === "draft";
  const canSend = hasRole(user, "manager") && po.status === "draft" && lines.length > 0;
  const raisedByName = user?.first_name ? `${user.first_name} ${user.last_name ?? ""}`.trim() : user?.login_email ?? "Coretel Group";
  // 28 Oct 2026 — James: "I just want the PDF downloaded so I can attach it and
  // email it" — the PDF saves to the PO (versioned) AND downloads to the
  // browser, named PO-26-0007_v5.pdf. Send-by-email stays as the secondary
  // button until the sender domain is verified.
  async function makePdf(open = true) {
    setPdfBusy(true);
    try {
      const r = await generatePoPdf(po.po_id, { name: raisedByName }); qc.invalidateQueries({ queryKey: ["po", poId] });
      if (open) {
        const resp = await fetch(r.signedUrl); const blob = await resp.blob();
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${String(po.po_number).replace(/[^A-Za-z0-9_-]/g, "-")}_v${r.version}.pdf`;
        document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      }
      return r;
    }
    catch (err: any) { alert(`PDF failed: ${err?.message ?? err}`); return null; }
    finally { setPdfBusy(false); }
  }

  // T-450 — PO vs actual: sum invoices coded to this PO and flag any delta.
  const invoicedTotal = (linkedInvoices as any[]).reduce((a, i) => a + Number(i.amount_inc_gst ?? 0), 0);
  const invoicedDelta = Math.round((invoicedTotal - Number(po.total_inc_gst ?? 0)) * 100) / 100;
  const canReconcilePo = hasRole(user, "manager");

  return (
    <div className="max-w-[1100px] mx-auto p-4 md:p-6">
      <button onClick={() => navigate("/pos")} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft size={14}/> Purchase Orders
      </button>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[13px] text-accent">{po.po_number}</span>
            <Pill label={po.status} tone={statusTone(po.status)} />
          </div>
          <h1 className="text-[22px] font-semibold tracking-tight mt-1">{po.po_title}</h1>
          <div className="text-text-muted text-[13px] mt-1">
            {po.supplier?.legal_name ?? "—"}
            {po.project?.project_code && ` · ${po.project.project_code}`}
            {po.site?.site_code && ` · ${po.site.site_code}`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button icon={Download} disabled={pdfBusy} onClick={() => makePdf(true)} title={po.pdf_version ? `v${po.pdf_version} made ${fmtDate(po.pdf_generated_at)} — this makes and downloads v${po.pdf_version + 1}` : "Make and download the PDF"}>
            {pdfBusy ? "Making…" : po.pdf_version ? `Download PDF (v${po.pdf_version + 1})` : "Download PDF"}
          </Button>
          {po.pdf_storage_path && (
            <Button icon={ExternalLink} variant="ghost" size="sm" onClick={async () => { const u = await getLatestPoPdfUrl(po.po_id); if (u) window.open(u, "_blank", "noopener"); }} title={`Open the saved v${po.pdf_version}`}>open v{po.pdf_version}</Button>
          )}
          {/* 1 Nov 2026 — James: "the PO does not need to be sent in Ops; it is a
              reply email we send off the RFQ one, so wasted in Ops." The Send by
              email button is off the screen; SendPoModal and /pos/:id/send stay
              in the code for the day email from Ops comes back. Download PDF +
              Mark as sent is the road. */}
          {canSend && (
            <Button icon={CheckCircle2} variant="secondary" onClick={() => { if (confirm(`Mark ${po.po_number} as sent to ${po.supplier?.legal_name}? The lines lock.`)) statusMut.mutate("sent"); }} title="You emailed the PDF yourself — mark it sent so it commits to the budget">Mark as sent</Button>
          )}
          {po.status === "sent" && hasRole(user, "manager") && (
            <Button icon={CheckCircle2} variant="secondary" onClick={() => statusMut.mutate("received")}>Mark received</Button>
          )}
          {po.status === "received" && hasRole(user, "manager") && (
            <Button icon={DollarSign} variant="secondary" onClick={() => statusMut.mutate("invoiced")}>Mark invoiced</Button>
          )}
          {po.status === "invoiced" && hasRole(user, "manager") && (
            <Button icon={CheckCircle2} onClick={() => statusMut.mutate("paid")}>Mark paid</Button>
          )}
        </div>
      </div>

      {sendOpen && <SendPoModal po={po} raisedBy={raisedByName} userEmail={(user as any)?.login_email ?? ""} pdfBusy={pdfBusy} makePdf={() => makePdf(false)}
        onClose={() => setSendOpen(false)} onSent={() => { setSendOpen(false); qc.invalidateQueries({ queryKey: ["po", poId] }); }}
        markOnly={() => { setSendOpen(false); statusMut.mutate("sent"); }}/>}

      {/* 28 Oct 2026 (mig 470/471) — which code this order spends and what it
          does to it: current / committed / actual / remaining, and the line
          after this PO. Finalise short when nothing more is coming. */}
      <PoBudgetCard po={po} lines={lines} canEdit={hasRole(user, "manager") && po.status !== "cancelled"} onChanged={() => { qc.invalidateQueries({ queryKey: ["po", poId] }); qc.invalidateQueries({ queryKey: ["po_lines", poId] }); }}/>

      {/* 27 Oct 2026 — delivery & terms: what prints in the Deliver-to box and
          the strip. Quick pick site / depot / office, or type it. */}
      <DeliveryTermsCard po={po} canEdit={canEdit} onSaved={() => qc.invalidateQueries({ queryKey: ["po", poId] })}/>

      {/* Summary bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPI label="Subtotal" value={fmtMoney(po.subtotal_ex_gst)} sub="ex GST"/>
        <KPI label="GST" value={fmtMoney(po.gst_amount)} />
        <KPI label="Total" value={fmtMoney(po.total_inc_gst)} sub="inc GST" accent/>
        <KPI label="Lines" value={lines.length} />
      </div>

      {/* T-450 — invoice-vs-PO delta flag. When invoices coded to this PO don't
          match the PO total, surface it (POs used to go stale silently) and offer
          a one-click variance line so the PO reflects what was actually invoiced. */}
      {linkedInvoices.length > 0 && Math.abs(invoicedDelta) > 0.005 && (
        <div className={`mb-4 p-3 rounded-md border flex items-center justify-between gap-3 flex-wrap ${invoicedDelta > 0 ? "border-warn/50 bg-warn/10" : "border-line bg-ink-raised"}`}>
          <div className="flex items-center gap-2 text-[12px]">
            <AlertTriangle size={14} className={invoicedDelta > 0 ? "text-warn flex-shrink-0" : "text-text-muted flex-shrink-0"} />
            <span className="text-text">
              Invoiced <span className="font-mono">{fmtMoney(invoicedTotal)}</span> against a PO of <span className="font-mono">{fmtMoney(po.total_inc_gst)}</span> —{" "}
              <span className="font-semibold">{invoicedDelta > 0 ? `${fmtMoney(invoicedDelta)} over` : `${fmtMoney(-invoicedDelta)} under`}</span>
              <span className="text-text-muted"> ({linkedInvoices.length} invoice{linkedInvoices.length === 1 ? "" : "s"})</span>
            </span>
          </div>
          {canReconcilePo && (
            <Button
              size="sm" variant="secondary" icon={CheckCircle2}
              disabled={matchPoMut.isPending}
              onClick={() => {
                if (!confirm(`Add a variance line of ${fmtMoney(invoicedDelta)} so ${po.po_number} matches the ${fmtMoney(invoicedTotal)} invoiced?`)) return;
                matchPoMut.mutate(invoicedDelta);
              }}
            >
              {matchPoMut.isPending ? "Updating…" : "Update PO to match invoiced"}
            </Button>
          )}
        </div>
      )}

      {/* Linked booking — T-080. Surfaces when a project_bookings row was
          auto-created at PO time (or attached later). One-click jump back to
          the project Schedule so the booking can be edited. */}
      {po.linked_booking && (
        <div className="mb-4 p-3 rounded-md border border-accent/30 bg-accent/8 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-[12px]">
            <CheckCircle2 size={14} className="text-accent flex-shrink-0"/>
            <span className="text-text-muted">Linked on-site booking:</span>
            <span className="text-text font-medium">{po.linked_booking.title}</span>
            <span className="text-text-muted font-mono">
              {po.linked_booking.scheduled_start
                ? new Date(po.linked_booking.scheduled_start).toLocaleDateString()
                : "—"}
            </span>
            <Pill label={po.linked_booking.booking_type} tone="default" small />
            <Pill label={po.linked_booking.status} tone={po.linked_booking.status === "confirmed" ? "won" : po.linked_booking.status === "tentative" ? "pending" : "default"} small />
          </div>
          <Link
            to={`/projects/${po.project_id}/schedule`}
            className="text-[12px] text-accent hover:underline inline-flex items-center gap-1"
          >
            Open in Schedule →
          </Link>
        </div>
      )}

      {/* Line editor */}
      <Card>
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">PO lines</div>
            {canEdit && (
              <Button icon={Plus} size="sm" variant="ghost" onClick={() => addLineMut.mutate()}>
                Add line
              </Button>
            )}
          </div>
          {lines.length === 0 ? (
            <div className="text-[13px] text-text-muted py-4 text-center">
              No lines yet. {canEdit && "Click 'Add line' to begin."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead><tr className="border-b border-line text-[10px] uppercase tracking-wider2 text-text-muted">
                  <th className="text-left px-2 py-2 w-[40%]">Description</th>
                  <th className="text-left px-2 py-2">Budget line</th>
                  <th className="text-right px-2 py-2">Qty</th>
                  <th className="text-left px-2 py-2">Unit</th>
                  <th className="text-right px-2 py-2">Unit price</th>
                  <th className="text-right px-2 py-2">Line total</th>
                  <th className="px-2 py-2"></th>
                </tr></thead>
                <tbody>
                  {lines.map(l => (
                    <PoLineRow
                      key={l.po_line_id}
                      line={l}
                      budgetLines={budgetLines}
                      canEdit={canEdit}
                      onUpdate={patch => updateLineMut.mutate({ id: l.po_line_id, patch })}
                      onDelete={() => { if (confirm("Delete this line?")) deleteLineMut.mutate(l.po_line_id); }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Notes + terms */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <EditableTextCard
          label="Scope / description for supplier"
          value={po.scope_text ?? ""}
          canEdit={canEdit}
          onSave={(v) => supabase.from("purchase_orders").update({ scope_text: v }).eq("po_id", po.po_id).then(() => qc.invalidateQueries({ queryKey: ["po", poId] }))}
        />
        <EditableTextCard
          label="Notes to the supplier (prints under the lines)"
          value={po.notes_text ?? ""}
          canEdit={canEdit}
          onSave={(v) => supabase.from("purchase_orders").update({ notes_text: v }).eq("po_id", po.po_id).then(() => qc.invalidateQueries({ queryKey: ["po", poId] }))}
        />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Inline PO line editor row
// -----------------------------------------------------------------------------
function PoLineRow({ line, budgetLines, canEdit, onUpdate, onDelete }: any) {
  const [localDesc, setLocalDesc] = useState(line.description ?? "");
  const [localQty, setLocalQty] = useState(String(line.quantity ?? 1));
  const [localUnit, setLocalUnit] = useState(line.unit ?? "");
  const [localPrice, setLocalPrice] = useState(String(line.unit_price_ex_gst ?? 0));
  const [localBudget, setLocalBudget] = useState(line.budget_line_id ?? "");

  React.useEffect(() => {
    setLocalDesc(line.description ?? "");
    setLocalQty(String(line.quantity ?? 1));
    setLocalUnit(line.unit ?? "");
    setLocalPrice(String(line.unit_price_ex_gst ?? 0));
    setLocalBudget(line.budget_line_id ?? "");
  }, [line.po_line_id]);

  const dirty =
    localDesc !== (line.description ?? "") ||
    Number(localQty) !== Number(line.quantity) ||
    localUnit !== (line.unit ?? "") ||
    Number(localPrice) !== Number(line.unit_price_ex_gst) ||
    localBudget !== (line.budget_line_id ?? "");

  function save() {
    onUpdate({
      description: localDesc,
      quantity: Number(localQty) || 0,
      unit: localUnit || null,
      unit_price_ex_gst: Number(localPrice) || 0,
      budget_line_id: localBudget || null,
      budget_code: localBudget ? budgetLines.find((b: any) => b.budget_line_id === localBudget)?.budget_code : null,
    });
  }

  return (
    <tr className="border-b border-line">
      <td className="px-2 py-1.5">
        <input disabled={!canEdit} value={localDesc} onChange={e => setLocalDesc(e.target.value)}
          className="w-full bg-transparent outline-none text-text text-[12px] border-b border-transparent focus:border-line"/>
      </td>
      <td className="px-2 py-1.5">
        <select disabled={!canEdit} value={localBudget} onChange={e => setLocalBudget(e.target.value)}
          className="w-full bg-transparent outline-none text-[11px] text-text-muted font-mono">
          <option value="">— unassigned —</option>
          {budgetLines.map((b: any) => (
            <option key={b.budget_line_id} value={b.budget_line_id}>
              {b.budget_code ?? "?"} · {b.description?.slice(0, 30)}
            </option>
          ))}
        </select>
      </td>
      <td className="px-2 py-1.5 text-right font-mono">
        <input disabled={!canEdit} type="number" value={localQty} onChange={e => setLocalQty(e.target.value)}
          className="w-16 bg-transparent outline-none text-text text-right border-b border-transparent focus:border-line"/>
      </td>
      <td className="px-2 py-1.5">
        <input disabled={!canEdit} value={localUnit} onChange={e => setLocalUnit(e.target.value)} placeholder="day, each, m..."
          className="w-16 bg-transparent outline-none text-text text-[12px] border-b border-transparent focus:border-line"/>
      </td>
      <td className="px-2 py-1.5 text-right font-mono">
        <input disabled={!canEdit} type="number" step="0.01" value={localPrice} onChange={e => setLocalPrice(e.target.value)}
          className="w-24 bg-transparent outline-none text-text text-right border-b border-transparent focus:border-line"/>
      </td>
      <td className="px-2 py-1.5 text-right font-mono">
        {fmtMoney((Number(localQty) || 0) * (Number(localPrice) || 0))}
      </td>
      <td className="px-2 py-1.5 text-right">
        {canEdit && dirty && <button onClick={save} className="text-accent hover:text-accent-bright p-1"><Save size={12}/></button>}
        {canEdit && !dirty && <button onClick={onDelete} className="text-text-dim hover:text-danger p-1"><Trash2 size={12}/></button>}
      </td>
    </tr>
  );
}

// -----------------------------------------------------------------------------
// Little inline-editable text card
// -----------------------------------------------------------------------------
function EditableTextCard({ label, value, canEdit, onSave }: any) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);
  React.useEffect(() => setV(value), [value]);

  return (
    <Card>
      <div className="p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">{label}</div>
          {canEdit && !editing && (
            <button onClick={() => setEditing(true)} className="text-text-muted hover:text-text"><Edit3 size={12}/></button>
          )}
        </div>
        {editing ? (
          <>
            <textarea value={v} onChange={e => setV(e.target.value)}
              className="w-full bg-ink border border-line rounded p-2 text-[13px] text-text min-h-[80px] outline-none focus:border-accent/40"/>
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="secondary" size="sm" onClick={() => { setV(value); setEditing(false); }}>Cancel</Button>
              <Button size="sm" icon={CheckCircle2} onClick={async () => { await onSave(v); setEditing(false); }}>Save</Button>
            </div>
          </>
        ) : (
          <div className="text-[13px] text-text whitespace-pre-wrap">
            {value || <span className="text-text-dim italic">not set</span>}
          </div>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Delivery & terms — 27 Oct 2026 (mig 464). James: "deliver address on plant
// POs should be the site address, materials the depot, labour our office —
// but nice to be able to quick pick or free text if need be."
// -----------------------------------------------------------------------------
function DeliveryTermsCard({ po, canEdit, onSaved }: { po: any; canEdit: boolean; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [kind, setKind] = useState<string>(po.po_kind ?? "other");
  const [dKind, setDKind] = useState<string>(po.deliver_to_kind ?? "site");
  const [dText, setDText] = useState<string>(po.deliver_to_text ?? "");
  const [scName, setScName] = useState<string>(po.site_contact_name ?? "");
  const [scPhone, setScPhone] = useState<string>(po.site_contact_phone ?? "");
  const [deliverBy, setDeliverBy] = useState<string>(po.required_by_date ?? "");
  const [terms, setTerms] = useState<string>(po.payment_terms_text ?? "");
  const [ccName, setCcName] = useState<string>(po.coretel_contact_name ?? "");
  const [ccPhone, setCcPhone] = useState<string>(po.coretel_contact_phone ?? "");
  const [showSup, setShowSup] = useState<boolean>(po.show_supplier_contact !== false);
  const [saving, setSaving] = useState(false);
  const { data: settings = {} } = useQuery({ queryKey: ["po_settings"], queryFn: loadPoSettings });
  // the project's people, so the Coretel contact is a click (PM first — James)
  const { data: team } = useQuery({
    queryKey: ["po_project_team", po.project_id], enabled: !!po.project_id,
    queryFn: async () => (await (supabase as any).from("projects").select("project_manager:project_manager_id(first_name, last_name, mobile), site_lead:site_lead_id(first_name, last_name, mobile), supervisor:supervisor_id(first_name, last_name, mobile)").eq("project_id", po.project_id).maybeSingle()).data as any,
  });
  const teamPicks = [["Project manager", team?.project_manager], ["Site lead", team?.site_lead], ["Supervisor", team?.supervisor]].filter(([, e]) => !!e) as [string, any][];
  React.useEffect(() => { if (!editing) { setShowSup(po.show_supplier_contact !== false); setKind(po.po_kind ?? "other"); setDKind(po.deliver_to_kind ?? "site"); setDText(po.deliver_to_text ?? ""); setScName(po.site_contact_name ?? ""); setScPhone(po.site_contact_phone ?? ""); setDeliverBy(po.required_by_date ?? ""); setTerms(po.payment_terms_text ?? ""); setCcName(po.coretel_contact_name ?? ""); setCcPhone(po.coretel_contact_phone ?? ""); } }, [po, editing]);

  const pick = (k: string) => { setDKind(k); if (k !== "other") setDText(deliverTextFor(k, po.site, settings as any)); };
  const pickKind = (k: string) => { setKind(k); if (k !== "other") pick(defaultDeliverKind(k)); };
  async function save() {
    setSaving(true);
    const { error } = await (supabase as any).from("purchase_orders").update({
      po_kind: kind, deliver_to_kind: dKind, deliver_to_text: dText.trim() || null, site_contact_name: scName.trim() || null, site_contact_phone: scPhone.trim() || null,
      required_by_date: deliverBy || null, payment_terms_text: terms.trim() || null, coretel_contact_name: ccName.trim() || null, coretel_contact_phone: ccPhone.trim() || null,
      show_supplier_contact: showSup,
    }).eq("po_id", po.po_id);
    setSaving(false);
    if (error) { alert(`Couldn't save: ${error.message}`); return; }
    setEditing(false); onSaved();
  }
  const inp = "w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text outline-none focus:border-accent/40";
  const lbl = "text-[10px] uppercase tracking-wider2 text-text-muted block mb-1";
  const chip = (on: boolean) => `px-2.5 py-1 rounded-full border text-[12px] ${on ? "border-accent bg-accent/10 text-accent" : "border-line text-text-muted hover:border-line-light"}`;
  const printed = (po.deliver_to_text?.trim() || deliverTextFor(po.deliver_to_kind, po.site, settings as any) || "—");

  return (
    <Card>
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold inline-flex items-center gap-2"><MapPin size={13} className="text-accent"/> Delivery & terms — what prints on the PO</div>
          {canEdit && !editing && <button onClick={() => setEditing(true)} className="text-text-muted hover:text-text"><Edit3 size={12}/></button>}
        </div>
        {!editing ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-[13px]">
            <div><div className={lbl}>Kind</div>{PO_KIND_LABEL[po.po_kind] ?? po.po_kind}</div>
            <div className="col-span-2 md:col-span-1"><div className={lbl}>Deliver to · {DELIVER_KIND_LABEL[po.deliver_to_kind] ?? po.deliver_to_kind}</div><div className="whitespace-pre-line">{printed}</div>{(po.site_contact_name || po.site_contact_phone) && <div className="text-text-muted text-[12px] mt-0.5">Site contact: {[po.site_contact_name, po.site_contact_phone].filter(Boolean).join(" ")}</div>}</div>
            <div><div className={lbl}>Deliver by</div>{po.required_by_date ? fmtDate(po.required_by_date) : <span className="text-text-dim">as agreed</span>}<div className={`${lbl} mt-2`}>Payment terms</div>{po.payment_terms_text || (settings as any).po_default_payment_terms || "30 days from invoice"}</div>
            <div><div className={lbl}>Coretel contact</div>{po.coretel_contact_name ? <>{po.coretel_contact_name}{po.coretel_contact_phone && <div className="text-text-muted text-[12px]">{po.coretel_contact_phone}</div>}</> : <span className="text-text-dim">the person who raises it</span>}<div className="text-[11px] text-text-dim mt-1">Supplier contact on the PDF: {po.show_supplier_contact === false ? "off" : "on"}</div></div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <span className={lbl}>Kind of order</span>
              <div className="flex gap-1.5 flex-wrap">{Object.entries(PO_KIND_LABEL).map(([k, l]) => <button key={k} type="button" className={chip(kind === k)} onClick={() => pickKind(k)}>{l}</button>)}</div>
            </div>
            <div>
              <span className={lbl}>Deliver to</span>
              <div className="flex gap-1.5 flex-wrap mb-2">
                {(["site", "depot", "other"] as const).map(k => <button key={k} type="button" className={chip(dKind === k)} onClick={() => pick(k)} disabled={k === "site" && !po.site} title={k === "site" && !po.site ? "No site on this project" : ""}>{DELIVER_KIND_LABEL[k]}</button>)}
              </div>
              <textarea className={`${inp} min-h-[64px]`} value={dText} onChange={e => { setDText(e.target.value); if (dKind !== "other") setDKind("other"); }} placeholder="Name and address, as it should print"/>
              <div className="text-[11px] text-text-dim mt-1">Typing over the address switches the pick to "Other" — the text is what prints.</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><span className={lbl}>Site contact</span><input className={inp} value={scName} onChange={e => setScName(e.target.value)} placeholder="name"/></div>
              <div><span className={lbl}>Site contact phone</span><input className={inp} value={scPhone} onChange={e => setScPhone(e.target.value)} placeholder="04…"/></div>
              <div><span className={lbl}>Deliver by</span><input className={inp} type="date" value={deliverBy} onChange={e => setDeliverBy(e.target.value)}/></div>
              <div><span className={lbl}>Payment terms</span><input className={inp} value={terms} onChange={e => setTerms(e.target.value)} placeholder={(settings as any).po_default_payment_terms || "30 days from invoice"}/></div>
              <div><span className={lbl}>Coretel contact</span><input className={inp} value={ccName} onChange={e => setCcName(e.target.value)} placeholder="who the supplier rings"/></div>
              <div><span className={lbl}>Their phone</span><input className={inp} value={ccPhone} onChange={e => setCcPhone(e.target.value)}/></div>
            </div>
            {teamPicks.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap text-[11.5px] text-text-muted">Use:
                {teamPicks.map(([role, e]) => <button key={role} type="button" className={chip(ccName === [e.first_name, e.last_name].filter(Boolean).join(" "))} onClick={() => { setCcName([e.first_name, e.last_name].filter(Boolean).join(" ")); setCcPhone(e.mobile ?? ""); }}>{role} · {e.first_name}</button>)}
              </div>
            )}
            <label className="flex items-center gap-2 text-[12.5px] cursor-pointer"><input type="checkbox" checked={showSup} onChange={e => setShowSup(e.target.checked)}/> Print the supplier's contact (name, email, phone) in the Supplier box</label>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
              <Button size="sm" icon={CheckCircle2} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Send to supplier — makes a fresh PDF, emails it from the worker (Resend,
// the HSE / acceptance pipe) with the sender cc'd, stamps sent. "Mark as sent
// only" for a PO that went by another road.
// -----------------------------------------------------------------------------
function SendPoModal({ po, raisedBy, userEmail, pdfBusy, makePdf, onClose, onSent, markOnly }: {
  po: any; raisedBy: string; userEmail: string; pdfBusy: boolean; makePdf: () => Promise<any>; onClose: () => void; onSent: () => void; markOnly: () => void;
}) {
  const [to, setTo] = useState<string>(po.supplier?.primary_contact_email ?? po.supplier?.billing_email ?? "");
  const [cc, setCc] = useState<string>(userEmail);
  const [message, setMessage] = useState<string>(`Hi ${po.supplier?.primary_contact_name?.split(" ")[0] ?? "there"},\n\nPlease find attached purchase order ${po.po_number} — ${po.po_title ?? ""}.\n\nQuote the PO number on your invoice. Give me a call if anything on it needs changing.\n\nThanks,\n${raisedBy}`);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to.trim());
  async function send() {
    setBusy(true); setErr(null);
    try {
      const made = await makePdf(); if (!made) throw new Error("The PDF didn't make — nothing sent.");
      const r = await apiFetch(`/pos/${po.po_id}/send`, { method: "POST", body: JSON.stringify({ to: to.trim(), cc: cc.trim() || null, message }) });
      const j: any = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.detail ?? j?.error ?? `send failed (${r.status})`);
      onSent();
    } catch (e: any) { setErr(e?.message ?? String(e)); } finally { setBusy(false); }
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={busy ? undefined : onClose}>
      <div className="bg-ink-card border border-line rounded-t-2xl sm:rounded-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-line flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-accent/12 flex items-center justify-center shrink-0"><Send size={18} className="text-accent"/></div>
          <div className="flex-1"><div className="text-[15px] font-semibold">Send {po.po_number} to {po.supplier?.trading_name ?? po.supplier?.legal_name ?? "the supplier"}</div><div className="text-[12px] text-text-muted mt-0.5">Makes a fresh PDF, emails it with you cc'd, and marks the PO sent (lines lock).</div></div>
          {!busy && <button onClick={onClose} className="text-text-muted hover:text-text"><X size={16}/></button>}
        </div>
        <div className="p-5 space-y-3">
          <Field label="To"><Input value={to} onChange={e => setTo(e.target.value)} placeholder="supplier's email"/></Field>
          <Field label="Cc"><Input value={cc} onChange={e => setCc(e.target.value)} placeholder="you"/></Field>
          <Field label="Message"><textarea className="w-full bg-ink border border-line rounded p-2 text-[13px] text-text min-h-[150px] outline-none focus:border-accent/40" value={message} onChange={e => setMessage(e.target.value)}/></Field>
          {!po.supplier?.primary_contact_email && !po.supplier?.billing_email && <div className="text-[11.5px] text-warn flex items-center gap-1.5"><AlertTriangle size={12}/> No email on the supplier record — type it here, and add it to the supplier so next time it's filled.</div>}
          {err && <div className="text-[12px] text-danger flex items-center gap-1.5"><AlertTriangle size={13}/>{err}</div>}
        </div>
        <div className="p-4 border-t border-line flex gap-2 items-center">
          <button className="text-[12px] text-text-muted hover:text-text underline-offset-2 hover:underline" onClick={() => { if (confirm(`Mark ${po.po_number} as sent without emailing it? The lines lock.`)) markOnly(); }} disabled={busy}>Mark as sent only (sent another way)</button>
          <span className="flex-1"/>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button icon={Send} disabled={busy || pdfBusy || !emailOk} onClick={send}>{busy ? "Making the PDF & sending…" : "Send"}</Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// PoBudgetCard — 28 Oct 2026 (mig 470/471). James, from Josh: "Which budget
// code is this PO consuming? How much is already committed? Spent? Remaining?
// Is this PO going to put the category over budget?" → "This PO will exceed
// the Subcontractor budget by $2,350" — cancel, proceed, or move money first.
// -----------------------------------------------------------------------------
function PoBudgetCard({ po, lines, canEdit, onChanged }: { po: any; lines: any[]; canEdit: boolean; onChanged: () => void }) {
  const qc = useQueryClient();
  const [code, setCode] = useState<string>(po.budget_code ?? "");
  React.useEffect(() => setCode(po.budget_code ?? ""), [po.budget_code]);
  const { data: codes = [] } = useQuery({
    queryKey: ["budget_codes_project"],
    queryFn: async () => { const { data } = await (supabase as any).from("budget_codes").select("budget_code, display_name, category, meaning, applies_to, sort_order").eq("is_active", true).order("sort_order"); return ((data ?? []) as any[]).filter(c => (c.applies_to ?? ["project"]).includes("project")); },
  });
  const { data: row } = useQuery({
    queryKey: ["po_budget_row", po.project_id, code],
    enabled: !!po.project_id && !!code,
    queryFn: async () => { const { data } = await (supabase as any).from("v_project_budget_codes").select("*").eq("project_id", po.project_id).eq("budget_code", code).maybeSingle(); return data as any | null; },
  });
  const setCodeMut = useMutation({
    mutationFn: async (c: string) => { const { error } = await (supabase as any).from("purchase_orders").update({ budget_code: c || null }).eq("po_id", po.po_id); if (error) throw new Error(error.message); },
    onSuccess: () => { onChanged(); qc.invalidateQueries({ queryKey: ["po_budget_row"] }); }, onError: (e: any) => alert(`Couldn't set the code: ${e.message}`),
  });
  const finaliseMut = useMutation({
    mutationFn: async (reopen: boolean) => { const { error } = await (supabase as any).schema("app").rpc("po_finalise", { p_po_id: po.po_id, p_reopen: reopen }); if (error) throw new Error(error.message); },
    onSuccess: () => { onChanged(); qc.invalidateQueries({ queryKey: ["po_budget_row"] }); qc.invalidateQueries({ queryKey: ["budget_codes_rows", po.project_id] }); qc.invalidateQueries({ queryKey: ["budget_header", po.project_id] }); },
    onError: (e: any) => alert(`Couldn't finalise: ${e.message}`),
  });
  const poEx = Number(po.subtotal_ex_gst ?? 0);
  const committedHere = po.status === "draft" ? 0 : poEx;   // once sent, this PO is already inside "committed"
  const after = row ? Number(row.remaining_ex) - (po.status === "draft" ? poEx : 0) : null;
  const over = after != null && after < -0.5;
  const mixed = lines.some(l => l.budget_code && l.budget_code !== code);
  const canFinalise = ["sent", "acknowledged", "received", "invoiced"].includes(po.status);
  const meaning = codes.find((c: any) => c.budget_code === code)?.meaning;
  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold inline-flex items-center gap-2"><DollarSign size={13} className="text-accent"/> Budget this order spends</div>
          <select className="bg-ink-raised border border-line rounded-md px-2.5 py-1.5 text-[13px] outline-none focus:border-accent min-w-[260px]" value={code} disabled={!canEdit} onChange={e => { setCode(e.target.value); setCodeMut.mutate(e.target.value); }}>
            <option value="">— pick a code —</option>
            {codes.map((c: any) => <option key={c.budget_code} value={c.budget_code}>{c.budget_code} · {c.display_name}</option>)}
          </select>
          {meaning && <span className="text-[11.5px] text-text-dim">{meaning}</span>}
          <span className="flex-1"/>
          {po.project_id && <Link to={`/projects/${po.project_id}?tab=financials`} className="text-[12px] text-accent hover:underline inline-flex items-center gap-1">the job's budget <ExternalLink size={11}/></Link>}
        </div>
        {!code ? (
          <div className="mt-3 text-[12.5px] text-warn flex items-center gap-2"><AlertTriangle size={14}/> No budget code — this order won't show against the job's budget until it has one.</div>
        ) : !row ? (
          <div className="mt-3 text-[12.5px] text-text-muted">No budget on this job for {code} yet — once sent, the order shows there as spend with no budget line (the manager can move money to it).</div>
        ) : (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2">
            <div className="rounded-lg bg-ink-raised p-2.5"><div className="text-[10px] uppercase tracking-wider2 text-text-muted">Current budget</div><div className="font-mono text-[14px]">{fmtMoney(row.current_ex)}</div></div>
            <div className="rounded-lg bg-ink-raised p-2.5"><div className="text-[10px] uppercase tracking-wider2 text-text-muted">Committed</div><div className="font-mono text-[14px] text-warn">{fmtMoney(row.committed_ex)}</div>{committedHere > 0 && !po.finalised_at && <div className="text-[10px] text-text-dim">incl. this PO</div>}</div>
            <div className="rounded-lg bg-ink-raised p-2.5"><div className="text-[10px] uppercase tracking-wider2 text-text-muted">Actual</div><div className="font-mono text-[14px]">{fmtMoney(row.actual_ex)}</div></div>
            <div className="rounded-lg bg-ink-raised p-2.5"><div className="text-[10px] uppercase tracking-wider2 text-text-muted">Remaining now</div><div className={`font-mono text-[14px] ${Number(row.remaining_ex) < 0 ? "text-danger" : "text-ok"}`}>{fmtMoney(row.remaining_ex)}</div></div>
            <div className={`rounded-lg p-2.5 ${over ? "bg-danger/10" : "bg-ok/10"}`}><div className="text-[10px] uppercase tracking-wider2 text-text-muted">{po.status === "draft" ? "After this PO" : "Remaining"}</div><div className={`font-mono text-[14px] ${over ? "text-danger" : "text-ok"}`}>{after != null ? fmtMoney(after) : "—"}</div>{over && po.status === "draft" && <div className="text-[10px] text-danger">over by {fmtMoney(-after!)}</div>}</div>
          </div>
        )}
        {over && po.status === "draft" && (
          <div className="mt-2 text-[12px] text-danger flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5 shrink-0"/><span>This PO will take <span className="font-mono">{code}</span> over its current budget by {fmtMoney(-after!)}. You can still send it — the job's budget will show the code over — or move money onto the code first from the project's Financials tab.</span></div>
        )}
        {mixed && <div className="mt-2 text-[11.5px] text-text-muted">Some lines carry their own code — the budget counts each line where its own code says.</div>}
        {canFinalise && (
          <div className="mt-3 pt-3 border-t border-line flex items-center gap-3 flex-wrap text-[12.5px]">
            {po.finalised_at ? (
              <><CheckCircle2 size={14} className="text-ok"/><span>Finalised {fmtDate(po.finalised_at)} — nothing more is coming; the uninvoiced balance is back in the budget.</span>
                {canEdit && <button className="text-accent hover:underline" onClick={() => finaliseMut.mutate(true)} disabled={finaliseMut.isPending}>reopen</button>}</>
            ) : (
              <><span className="text-text-muted">Supplier's done and the invoices are in?</span>
                {canEdit && <Button size="sm" variant="secondary" icon={CheckCircle2} disabled={finaliseMut.isPending} onClick={() => { if (confirm(`Finalise ${po.po_number}? Whatever hasn't been invoiced stops counting as committed and goes back to the budget.`)) finaliseMut.mutate(false); }}>{finaliseMut.isPending ? "…" : "Finalise — nothing more coming"}</Button>}</>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
